import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  ayarDegistir,
  ayarGecmisi,
  ayarListesi,
  ayarOnIzleme,
  DAYANAK_TIPLERI,
  type AyarKaynagi,
  type AyarSatiri,
  type DayanakTipi,
} from '../api/yonetim';
import { Diyalog } from '../bilesenler/Diyalog';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { CokSatir, Dugme, HataKutusu, Metin, Sekme } from '../bilesenler/temel';
import { bugun, tarihMetni } from '../bicim/tarih';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';
import { AyarlarTakvim } from './AyarlarTakvim';

/**
 * SEÇENEK ALANI VE TAKVİM.
 *
 * MENÜ GENİŞ, SEKMELER DAR. `GET /yonetim/takvim` `PERSONEL_GORUNTULE`yi de
 * kabul ediyor; menü ona kapatılsaydı ekip lideri erişebildiği ekranı hiç
 * göremezdi (SÖZLEŞME §11). Bu yüzden menü uçların BİRLEŞİMİ, her sekme kendi
 * ucunun listesine bakıyor.
 *
 * YAZMA YETKİSİ OKUMADAN DAR: `PARAMETRE_YONET` tek başına gerekiyor.
 * `SISTEM_YONET` sahibi ayarları görür ama değiştiremez; düğme ona
 * gösterilseydi 403 alırdı.
 */

const KAYNAK_ETIKETI: Record<AyarKaynagi, string> = {
  sistem_parametre: 'Sistem parametresi',
  mesai_katsayi: 'Mesai katsayısı',
  mesai_limit: 'Mesai limiti',
};

const DAYANAK_ETIKETI: Record<DayanakTipi, string> = {
  kanun: 'Kanun',
  yonetmelik: 'Yönetmelik',
  yargitay_ictihadi: 'Yargıtay içtihadı',
  tis: 'Toplu iş sözleşmesi',
  meclis_karari: 'Meclis kararı',
  baskanlik_onayi: 'Başkanlık onayı',
  ik_karari: 'İK kararı',
  mudur_onayi: 'Müdür onayı',
  hukuk_musavirligi_gorusu: 'Hukuk müşavirliği görüşü',
};

type SekmeKodu = 'ayar' | 'takvim';

export function Ayarlar() {
  const yetkilerHazir = yetkilerHazirKullan();

  const ayarGorebilir = yetkiKullan('PARAMETRE_YONET', 'SISTEM_YONET');
  const takvimGorebilir = yetkiKullan('PERSONEL_GORUNTULE', 'PARAMETRE_YONET', 'SISTEM_YONET');
  // YAZMA TEK KODA BAĞLI — üç yazma ucu da yalnız `PARAMETRE_YONET` kabul ediyor.
  const yazabilir = yetkiKullan('PARAMETRE_YONET');

  // Sekme türetilir; yetkiler asenkron geliyor (SÖZLEŞME §5).
  const [sekmeSecimi, sekmeSecimiYaz] = useState<SekmeKodu | null>(null);
  const sekme: SekmeKodu = sekmeSecimi ?? (ayarGorebilir ? 'ayar' : 'takvim');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Ayarlar ve Takvim</h1>
        <p className="mt-1 text-sm text-slate-600">
          Hesap kuralları DAYANAKSIZ değiştirilemez: her değişiklik belge tipi, numarası, tarihi ve
          gerekçe ister. Eski değer silinmez, sürümlenir.
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        {ayarGorebilir && (
          <Sekme etkin={sekme === 'ayar'} tikla={() => sekmeSecimiYaz('ayar')} etiket="Ayarlar" />
        )}
        {takvimGorebilir && (
          <Sekme
            etkin={sekme === 'takvim'}
            tikla={() => sekmeSecimiYaz('takvim')}
            etiket="Takvim"
          />
        )}
      </div>

      {!yetkilerHazir ? (
        <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>
      ) : sekme === 'ayar' && ayarGorebilir ? (
        <AyarListesi yazabilir={yazabilir} />
      ) : takvimGorebilir ? (
        <AyarlarTakvim yazabilir={yazabilir} yetkilerHazir={yetkilerHazir} />
      ) : (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Bu ekranı görüntüleme yetkiniz yok.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ayar listesi                                                        */
/* ------------------------------------------------------------------ */

function AyarListesi({ yazabilir }: { yazabilir: boolean }) {
  const [yalnizDogrulanmamis, yalnizDogrulanmamisYaz] = useState(false);
  const [acikGecmis, acikGecmisYaz] = useState<AyarSatiri | null>(null);
  const [degistirilen, degistirilenYaz] = useState<AyarSatiri | null>(null);

  const liste = useQuery({
    queryKey: ['yonetim', 'ayar', { yalnizDogrulanmamis }],
    queryFn: () => ayarListesi(yalnizDogrulanmamis),
  });

  const sutunlar: Sutun<AyarSatiri>[] = [
    {
      baslik: 'Ayar',
      hucre: (a) => (
        <span>
          <span className="block font-medium text-slate-900">{a.ad}</span>
          <span className="block text-xs text-slate-500">
            {KAYNAK_ETIKETI[a.kaynak]} · <code>{a.kod}</code>
          </span>
        </span>
      ),
    },
    {
      baslik: 'Değer',
      hucre: (a) => (
        <span className="font-mono text-xs">
          {a.deger}
          {a.birim !== null && <span className="ml-1 text-slate-500">{a.birim}</span>}
        </span>
      ),
      daralt: true,
    },
    {
      baslik: 'Dayanak',
      hucre: (a) =>
        a.dogrulanmisMi ? (
          <span>
            <span className="block text-xs">{a.dayanakTipi}</span>
            {a.dayanakNo !== null && (
              <span className="block text-xs text-slate-500">
                {a.dayanakNo}
                {a.dayanakTarihi !== null && ` · ${tarihMetni(a.dayanakTarihi)}`}
              </span>
            )}
          </span>
        ) : (
          /*
            DOĞRULANMAMIŞ SATIR VURGULANIR. Bu üç satır dönem kilidini
            ENGELLİYOR; diğerleriyle aynı görünselerdi yönetici hangisinin
            kilidi durdurduğunu listede göremezdi.
          */
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            Onaysız — kilidi engeller
          </span>
        ),
    },
    {
      baslik: 'Yürürlük',
      hucre: (a) => tarihMetni(a.gecerlilikBaslangic),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: '',
      hucre: (a) => (
        <span className="flex justify-end gap-1 whitespace-nowrap">
          <button
            type="button"
            onClick={() => acikGecmisYaz(a)}
            className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
          >
            Geçmiş
          </button>
          {yazabilir && (
            <button
              type="button"
              onClick={() => degistirilenYaz(a)}
              className="rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
            >
              Değiştir
            </button>
          )}
        </span>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={yalnizDogrulanmamis}
          onChange={(olay) => yalnizDogrulanmamisYaz(olay.target.checked)}
          className="rounded border-slate-300"
        />
        Yalnız dönem kilidini engelleyenler
      </label>

      <HataKutusu hata={liste.error} />

      {/*
        SAYFALAMA YOK: uç düz dizi döndürüyor, sayfalı değil. `Tablo` ile
        `Sayfalama`nın ayrı bileşen olması (SÖZLEŞME §13) burada da işe yarıyor.
      */}
      <Tablo
        sutunlar={sutunlar}
        satirlar={liste.data ?? []}
        anahtar={(a) => `${a.kaynak}:${a.kod}`}
        bekliyor={liste.isPending}
        bosMetin={
          yalnizDogrulanmamis ? 'Onaysız ayar kalmadı.' : 'Değiştirilebilir ayar bulunamadı.'
        }
      />

      {acikGecmis !== null && (
        <GecmisDiyalogu ayar={acikGecmis} kapat={() => acikGecmisYaz(null)} />
      )}

      {degistirilen !== null && (
        <AyarDegistirDiyalogu ayar={degistirilen} kapat={() => degistirilenYaz(null)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sürüm geçmişi                                                       */
/* ------------------------------------------------------------------ */

function GecmisDiyalogu({ ayar, kapat }: { ayar: AyarSatiri; kapat: () => void }) {
  const gecmis = useQuery({
    queryKey: ['yonetim', 'ayar-gecmis', ayar.kaynak, ayar.kod],
    queryFn: () => ayarGecmisi(ayar.kaynak, ayar.kod),
  });

  return (
    <Diyalog
      baslik={`${ayar.ad} — sürüm geçmişi`}
      aciklama="Ne zaman, hangi belgeyle, hangi değer. Eski satırlar silinmez."
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <HataKutusu hata={gecmis.error} />

          {gecmis.isPending ? (
            <p className="text-sm text-slate-500">Yükleniyor…</p>
          ) : (
            <ol className="space-y-3">
              {gecmis.data?.map((s) => (
                <li
                  key={`${s.gecerlilikBaslangic}:${s.deger}`}
                  className={`border-l-2 pl-3 ${
                    // `gecerlilikBitis === null` YÜRÜRLÜKTEKİ sürümdür.
                    s.gecerlilikBitis === null ? 'border-kurum-500' : 'border-slate-200'
                  }`}
                >
                  <p className="text-sm">
                    <span className="font-mono font-medium text-slate-900">{s.deger}</span>
                    <span className="ml-2 text-xs text-slate-500">
                      {tarihMetni(s.gecerlilikBaslangic)} —{' '}
                      {s.gecerlilikBitis === null ? 'yürürlükte' : tarihMetni(s.gecerlilikBitis)}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-600">
                    {s.dayanakTipi}
                    {s.dayanakNo !== null && ` · ${s.dayanakNo}`}
                  </p>
                  {s.aciklama !== null && (
                    <p className="mt-0.5 text-xs text-slate-500">{s.aciklama}</p>
                  )}
                </li>
              ))}
            </ol>
          )}

          <div className="flex justify-end">
            <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
          </div>
        </div>
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/* Ayar değiştirme                                                     */
/* ------------------------------------------------------------------ */

/**
 * AYAR DEĞİŞTİRME — ÖN İZLEME OLMADAN KAYDEDİLEMEZ.
 *
 * `GerekceDiyalogu` KULLANILMADI: bu form altı alan istiyor (yeni değer,
 * yürürlük tarihi, dayanak tipi/no/tarihi, gerekçe). O bileşene altı alan
 * eklemek onu genel olmaktan çıkarırdı — asıl kullanıcıları izin reddi ve
 * dönem kilidi. Ortak `Diyalog` kabuğu kullanılıyor.
 *
 * ÖN İZLEME KAYDET DÜĞMESİNİ KİLİTLER. Kontrolcünün gerekçesi: 40 kişinin iki
 * aylık puantajını değiştiren bir işlem tek tıkla yapılmamalı. Ön izlemeyi
 * geçersizleştiren her alan değişikliğinde kilit geri gelir.
 */
function AyarDegistirDiyalogu({ ayar, kapat }: { ayar: AyarSatiri; kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const [yeniDeger, yeniDegerYaz] = useState(ayar.deger);
  const [gecerlilikBaslangic, gecerlilikBaslangicYaz] = useState(() => bugun());
  const [dayanakTipi, dayanakTipiYaz] = useState<DayanakTipi | ''>('');
  const [dayanakNo, dayanakNoYaz] = useState('');
  const [dayanakTarihi, dayanakTarihiYaz] = useState(() => bugun());
  const [gerekce, gerekceYaz] = useState('');
  const [aciklama, aciklamaYaz] = useState('');

  const [onIzleme, onIzlemeYaz] = useState<{
    /** Ön izlemenin ait olduğu tarih. Değişirse sonuç GEÇERSİZDİR. */
    tarih: string;
    mevcutDeger: string;
    etkilenenGun: number;
    uyarilar: string[];
  } | null>(null);

  const onIzlemeGecerli = onIzleme !== null && onIzleme.tarih === gecerlilikBaslangic;

  const izle = useMutation({
    mutationFn: () => ayarOnIzleme({ kaynak: ayar.kaynak, kod: ayar.kod, gecerlilikBaslangic }),
    onSuccess: (sonuc) =>
      onIzlemeYaz({
        tarih: gecerlilikBaslangic,
        mevcutDeger: sonuc.mevcutDeger,
        etkilenenGun: sonuc.etkilenenGun,
        uyarilar: sonuc.uyarilar,
      }),
  });

  const kaydet = useMutation({
    mutationFn: () =>
      ayarDegistir({
        kaynak: ayar.kaynak,
        kod: ayar.kod,
        yeniDeger: yeniDeger.trim(),
        gecerlilikBaslangic,
        dayanakTipi: dayanakTipi as DayanakTipi,
        dayanakNo: dayanakNo.trim(),
        dayanakTarihi,
        gerekce: gerekce.trim(),
        aciklama: aciklama.trim() === '' ? undefined : aciklama.trim(),
      }),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['yonetim', 'ayar'] });
      // Rehberdeki "onaysız parametre" sayacı da bu değişiklikten etkilenir.
      await sorguIstemcisi.invalidateQueries({ queryKey: ['rehber'] });
      kapat();
    },
  });

  const alanlarDolu =
    yeniDeger.trim() !== '' &&
    yeniDeger.trim() !== ayar.deger &&
    dayanakTipi !== '' &&
    dayanakNo.trim() !== '' &&
    gerekce.trim() !== '';

  const kaydedilebilir = alanlarDolu && onIzlemeGecerli;

  /** Ön izlemeyi geçersizleştiren alan değişince kilit geri gelir. */
  const tarihiYaz = (yeni: string) => {
    gecerlilikBaslangicYaz(yeni);
  };

  return (
    <Diyalog
      baslik={`${ayar.ad} değerini değiştir`}
      aciklama={`${KAYNAK_ETIKETI[ayar.kaynak]} · ${ayar.kod} · şu anki değer: ${ayar.deger}`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Metin
              etiket="Yeni değer"
              deger={yeniDeger}
              degisti={yeniDegerYaz}
              ipucu="Sayısal ayarlar da METİN taşır; ondalık kaybı olmasın diye."
            />
            <Metin
              etiket="Yürürlük başlangıcı"
              tur="date"
              deger={gecerlilikBaslangic}
              degisti={tarihiYaz}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Dayanak tipi</span>
              <select
                value={dayanakTipi}
                onChange={(olay) => dayanakTipiYaz(olay.target.value as DayanakTipi | '')}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
              >
                <option value="">Seçin…</option>
                {DAYANAK_TIPLERI.map((t) => (
                  <option key={t} value={t}>
                    {DAYANAK_ETIKETI[t]}
                  </option>
                ))}
              </select>
              {/*
                `oneri_varsayilan` LİSTEDE YOK ve bu bilinçli. Seçilebilseydi
                doğrulanmış bir parametre öneriye geri çevrilir ve dönem kilidi
                farkında olmadan kilitlenirdi.
              */}
            </label>

            <Metin etiket="Belge no" deger={dayanakNo} degisti={dayanakNoYaz} />
            <Metin
              etiket="Belge tarihi"
              tur="date"
              deger={dayanakTarihi}
              degisti={dayanakTarihiYaz}
            />
          </div>

          <CokSatir
            etiket="Gerekçe"
            deger={gerekce}
            degisti={gerekceYaz}
            ipucu="Zorunlu — denetim izine yazılır ve kalıcı kayıttır."
          />

          <CokSatir
            etiket="Açıklama (isteğe bağlı)"
            deger={aciklama}
            degisti={aciklamaYaz}
            satir={2}
          />

          <HataKutusu hata={izle.error} />
          <HataKutusu hata={kaydet.error} />

          {onIzlemeGecerli && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
              <p className="text-slate-800">
                Mevcut değer <span className="font-mono">{onIzleme.mevcutDeger}</span> ·{' '}
                <span className={onIzleme.etkilenenGun > 0 ? 'font-semibold text-amber-800' : ''}>
                  {onIzleme.etkilenenGun} günlük puantaj
                </span>{' '}
                etkilenir.
              </p>
              {onIzleme.uyarilar.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-slate-700">
                  {onIzleme.uyarilar.map((u) => (
                    <li key={u}>{u}</li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-xs text-slate-500">
                Hesap OTOMATİK KOŞMAZ; yeniden hesaplama ayrıca tetiklenir.
              </p>
            </div>
          )}

          {!onIzlemeGecerli && (
            <p className="text-xs text-slate-500">
              Kaydetmeden önce etkiyi görmeniz gerekir. Yürürlük tarihini değiştirirseniz ön izleme
              yenilenmelidir.
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={kaydet.isPending} cocuk="Vazgeç" />
            <Dugme
              tur="ikincil"
              tikla={() => izle.mutate()}
              bekliyor={izle.isPending}
              cocuk="Etkiyi göster"
            />
            <Dugme
              tikla={() => kaydet.mutate()}
              bekliyor={kaydet.isPending}
              pasif={!kaydedilebilir}
              cocuk="Kaydet"
            />
          </div>
        </div>
      }
    />
  );
}
